import * as React from "react";
import "./uploadVehiclePhotos.css";
import { getPhotoTemplate } from "./utils/getPhotoTemplate";
import { getVehicleType } from "./utils/getVehicleType";
import {
    fetchVehiclePhotos,
    fetchPhotoSummary,
    uploadSinglePhoto,
    uploadMultiplePhotos,
    deletePhoto,
    updatePhoto
} from "./utils/photoApi";
import { downloadSinglePhoto, downloadAllPhotosAsZip } from "./utils/photoDownload";
import {
    createFilePreview,
    findPhotoByTemplate,
    validateImageFile,
    buildBulkUploadData
} from "./utils/photoHelpers";
import { searchVehicle } from "./utils/vehicleSearch";
import { groupPhotosByCategory, countPhotosByCategory } from "./utils/photoCategorization";
import PhotoSlot from "./PhotoSlot";
import BulkPhotoUpload from "./BulkPhotoUpload";
import PhotoDashboard from "./PhotoDashboard";
import axios from "axios";

function UploadVehiclePhotos({ stockNum }) {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [vehicleId, setVehicleId] = React.useState(null);
    const [vehicle, setVehicle] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState("");
    const [searchType] = React.useState("stock");
    const [uploadedPhotos, setUploadedPhotos] = React.useState({});
    const [uploading, setUploading] = React.useState(false);
    const [existingPhotos, setExistingPhotos] = React.useState([]);
    const [loadingPhotos, setLoadingPhotos] = React.useState(false);
    const [deletingPhotos, setDeletingPhotos] = React.useState({});
    const [updatingPhotos, setUpdatingPhotos] = React.useState({});
    const [downloadingPhotos, setDownloadingPhotos] = React.useState({});
    const [downloadingAll, setDownloadingAll] = React.useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);
    const [photoSummary, setPhotoSummary] = React.useState({});
    const [loadingSummary, setLoadingSummary] = React.useState(true);
    const apiUrl = process.env.REACT_APP_API_URL;

    React.useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const inventoryRes = await axios.get(`${apiUrl}/api/admin/vehicle/list`);
                const vehicles = inventoryRes.data;
                setData(vehicles);
                setLoading(false);

                // Fetch photo summary (passes vehicle list for fallback per-vehicle checks)
                const summaryRes = await fetchPhotoSummary(vehicles);
                setPhotoSummary(summaryRes);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Failed to fetch vehicle data");
                setLoading(false);
            } finally {
                setLoadingSummary(false);
            }
        };

        fetchInitialData();
    }, [apiUrl]);


    // Fetch existing photos for a vehicle
    const fetchExistingPhotos = React.useCallback(async (vehicleId) => {
        setLoadingPhotos(true);
        try {
            const photos = await fetchVehiclePhotos(vehicleId);
            setExistingPhotos(photos);
        } catch (error) {
            setError(error.message);
            setExistingPhotos([]);
        } finally {
            setLoadingPhotos(false);
        }
    }, []);

    const handleSearch = React.useCallback(() => {
        if (!searchValue.trim()) {
            alert("Please enter a search value");
            return;
        }

        const foundVehicle = searchVehicle(data, searchType, searchValue);

        if (foundVehicle.error) {
            setError(foundVehicle.error);
            alert(`Vehicle not found with ${searchType}: ${searchValue}`);
            setVehicleId(null);
            setVehicle(null);
            setExistingPhotos([]);
        } else {
            setVehicleId(foundVehicle.id);
            setVehicle(foundVehicle);
            setError(null);
            setUploadedPhotos({});
            fetchExistingPhotos(foundVehicle.id);
            console.log("Found vehicle:", foundVehicle);
        }
    }, [searchValue, searchType, data, fetchExistingPhotos]);

    React.useEffect(() => {
        if (!loading && data && data.length > 0 && stockNum) {
            const foundVehicle = searchVehicle(data, 'stock', stockNum);
            if (foundVehicle.error) {
                setError(foundVehicle.error);
                alert(`Vehicle not found with stock number: ${stockNum}`);
            } else {
                setVehicleId(foundVehicle.id);
                setVehicle(foundVehicle);
                fetchExistingPhotos(foundVehicle.id);
                console.log("Found vehicle:", foundVehicle);
            }
        }
    }, [loading, data, stockNum, fetchExistingPhotos]);

    const processFile = async (file, photoTemplate, subTag = null) => {
        const validation = validateImageFile(file);
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        try {
            const preview = await createFilePreview(file);
            const key = subTag != null
                ? `${photoTemplate.name}::${subTag}`
                : photoTemplate.name;
            setUploadedPhotos(prev => ({
                ...prev,
                [key]: {
                    file: file,
                    preview: preview,
                    template: photoTemplate,
                    subTag: subTag
                }
            }));
        } catch (error) {
            alert('Failed to process image file');
            console.error(error);
        }
    };

    const handleUploadToCloud = async () => {
        if (Object.keys(uploadedPhotos).length === 0) {
            alert("Please select at least one photo to upload");
            return;
        }

        setUploading(true);
        try {
            const photoEntries = Object.entries(uploadedPhotos);

            // Use bulk upload if there are multiple photos
            if (photoEntries.length > 1) {
                const { files, metadata } = buildBulkUploadData(vehicleId, uploadedPhotos);

                console.log('Bulk uploading', photoEntries.length, 'photos for vehicle', vehicleId);

                const response = await uploadMultiplePhotos({
                    vehicleId,
                    files,
                    photoMetadata: metadata
                });

                if (response.errors && response.errors.length > 0) {
                    console.warn('Some photos failed to upload:', response.errors);
                    alert(`Uploaded ${response.data.length} photos successfully. ${response.errors.length} failed.`);
                } else {
                    alert(`Successfully uploaded ${photoEntries.length} photos!`);
                }
            } else {
                // Single photo upload
                const [key, photoData] = photoEntries[0];
                const separatorIndex = key.indexOf('::');
                const photoName = separatorIndex !== -1 ? key.slice(0, separatorIndex) : key;
                const subTag = photoData.subTag != null
                    ? photoData.subTag
                    : separatorIndex !== -1 ? key.slice(separatorIndex + 2) : null;

                await uploadSinglePhoto({
                    file: photoData.file,
                    vehicleId,
                    photoName,
                    subTag,
                    category: photoData.template.category,
                    description: photoData.template.description,
                    tags: photoData.template.tags || [],
                    templateId: photoData.template.id
                });

                console.log(`Uploaded ${photoName} for vehicle ${vehicleId}`);
                alert('Successfully uploaded photo!');
            }

            setUploadedPhotos({});
            // Refresh existing photos list and summary
            await fetchExistingPhotos(vehicleId);
            const updatedSummary = await fetchPhotoSummary(data);
            setPhotoSummary(updatedSummary);
        } catch (error) {
            console.error("Error uploading photos:", error);
            alert(`Error uploading photos: ${error.response?.data?.error || error.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleBulkAssign = async (assignments) => {
        if (!assignments || assignments.length === 0) return;

        setUploading(true);
        try {
            const files = assignments.map(a => a.file);
            const metadata = assignments.map(a => {
                const meta = {
                    photoName: a.template.name,
                    subTag: a.subTag ?? null,
                    category: a.template.category,
                    description: a.template.description,
                    tags: a.template.tags || []
                };
                if (a.template.id) meta.templateId = a.template.id;
                return meta;
            });

            const response = await uploadMultiplePhotos({ vehicleId, files, photoMetadata: metadata });

            setBulkUploadOpen(false);

            if (response.errors && response.errors.length > 0) {
                alert(`Uploaded ${response.data.length} photos successfully. ${response.errors.length} failed.`);
            }

            setUploadedPhotos({});
            await fetchExistingPhotos(vehicleId);
            const updatedSummary = await fetchPhotoSummary(data);
            setPhotoSummary(updatedSummary);
        } catch (error) {
            console.error("Error uploading bulk photos:", error);
            const detail = error.response?.data?.details || error.response?.data?.errors?.[0]?.error || error.response?.data?.error || error.message;
            alert(`Error uploading photos: ${detail}`);
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = (photoName) => {
        setUploadedPhotos(prev => {
            const updated = { ...prev };
            delete updated[photoName];
            return updated;
        });
    };

    // Delete existing photo from cloud and database
    const handleDeleteExistingPhoto = async (fileId, photoName) => {
        if (!window.confirm(`Are you sure you want to delete "${photoName}"? This will remove all versions from the cloud storage and cannot be undone.`)) {
            return;
        }

        setDeletingPhotos(prev => ({ ...prev, [fileId]: true }));
        try {
            const response = await deletePhoto(fileId);

            if (response.success) {
                alert(`Successfully deleted "${photoName}" and ${response.deletedCount} file(s) from cloud storage`);
                // Refresh the existing photos list
                await fetchExistingPhotos(vehicleId);
            }
        } catch (error) {
            console.error("Error deleting photo:", error);
            alert(`Failed to delete photo: ${error.response?.data?.error || error.message}`);
        } finally {
            setDeletingPhotos(prev => {
                const updated = { ...prev };
                delete updated[fileId];
                return updated;
            });
        }
    };

    // Update existing photo with a new file
    const handleUpdateExistingPhoto = async (file, existingPhoto, template) => {
        setUpdatingPhotos(prev => ({ ...prev, [existingPhoto.file_id]: true }));
        try {
            const response = await updatePhoto({
                fileId: existingPhoto.file_id,
                file,
                photoName: existingPhoto.photo_name,
                category: existingPhoto.category,
                description: existingPhoto.description,
                tags: existingPhoto.tags,
                templateId: template?.id || existingPhoto.template_id
            });

            if (response.success) {
                alert(`Successfully updated "${existingPhoto.photo_name}"`);
                await fetchExistingPhotos(vehicleId);
            }
        } catch (error) {
            console.error("Error updating photo:", error);
            alert(`Failed to update photo: ${error.response?.data?.error || error.message}`);
        } finally {
            setUpdatingPhotos(prev => {
                const updated = { ...prev };
                delete updated[existingPhoto.file_id];
                return updated;
            });
        }
    };

    // Download a single existing photo at full resolution
    const handleDownloadPhoto = async (photo) => {
        setDownloadingPhotos(prev => ({ ...prev, [photo.file_id]: true }));
        try {
            await downloadSinglePhoto(photo, vehicle);
        } catch (error) {
            console.error("Error downloading photo:", error);
            alert(`Failed to download photo: ${error.message}`);
        } finally {
            setDownloadingPhotos(prev => {
                const updated = { ...prev };
                delete updated[photo.file_id];
                return updated;
            });
        }
    };

    // Download all existing photos for the vehicle as a single zip
    const handleDownloadAll = async () => {
        if (!existingPhotos || existingPhotos.length === 0) return;

        setDownloadingAll(true);
        try {
            const { successCount, failures } = await downloadAllPhotosAsZip(existingPhotos, vehicle);
            if (failures.length > 0) {
                alert(`Downloaded ${successCount} of ${existingPhotos.length} photos as a zip. ${failures.length} photo(s) failed: ${failures.map(f => f.photo.photo_name).join(', ')}`);
            }
        } catch (error) {
            console.error("Error downloading all photos:", error);
            alert(`Failed to create zip: ${error.message}`);
        } finally {
            setDownloadingAll(false);
        }
    };

    const handleSelectVehicle = React.useCallback((selectedVehicle) => {
        setVehicleId(selectedVehicle.id);
        setVehicle(selectedVehicle);
        setSearchValue(selectedVehicle.stock);
        setError(null);
        setUploadedPhotos({});
        fetchExistingPhotos(selectedVehicle.id);
    }, [fetchExistingPhotos]);

    const handleClearVehicle = () => {
        setVehicle(null);
        setVehicleId(null);
        setSearchValue("");
        setExistingPhotos([]);
        setUploadedPhotos({});
        setError(null);
    };

    // Helper to find existing photo by template (uses ID if available, falls back to name)
    const getExistingPhotoByTemplate = (photoTemplate) => {
        return findPhotoByTemplate(existingPhotos, photoTemplate);
    };



    const currentPhotoTemplate = vehicle ? getPhotoTemplate(getVehicleType(vehicle)) : [];
    const { exterior: exteriorPhotos, interior: interiorPhotos, detail: detailPhotos } = groupPhotosByCategory(currentPhotoTemplate);
    const { exterior: existingExteriorCount, interior: existingInteriorCount, detail: existingDetailCount } = countPhotosByCategory(existingPhotos);
    

    if (loading) {
        return <div className="upload-container">Loading vehicle data...</div>;
    }

    return (
        <div className="upload-container">
            <h1>Vehicle Photo Upload</h1>

            {/* Search Section */}
            <div className="search-section">
                <h2>Find Vehicle</h2>
                <div className="search-form">
                    <div className="search-input-group">
                        <input
                            type="text"
                            id="photo-search-stock"
                            name="stockNumber"
                            className="search-input"
                            placeholder="Enter Stock Number"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <button
                            className="search-button"
                            onClick={handleSearch}
                            disabled={loading || !searchValue}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Photo Dashboard - shown when no vehicle is selected */}
            {!vehicle && !loading && (
                <PhotoDashboard
                    vehicles={data}
                    photoSummary={photoSummary}
                    loadingSummary={loadingSummary}
                    searchFilter={searchValue}
                    onSelectVehicle={handleSelectVehicle}
                />
            )}

            {error && !vehicle && (
                <div className="error-message">{error}</div>
            )}

            {/* Vehicle Info Section */}
            {vehicle && (
                <div className="vehicle-info-section">
                    <div className="vehicle-info-header">
                        <h2>Selected Vehicle</h2>
                        {!stockNum && (
                            <button className="clear-vehicle-btn" onClick={handleClearVehicle}>
                                ← Back to Dashboard
                            </button>
                        )}
                    </div>
                    <div className="vehicle-details">
                        <p><strong>ID:</strong> {vehicle.id}</p>
                        <p><strong>Stock:</strong> {vehicle.stock}</p>
                        <p><strong>VIN:</strong> {vehicle.vin}</p>
                        <p><strong>Vehicle:</strong> {vehicle.year} {vehicle.make} {vehicle.model}</p>
                        <p><strong>Color:</strong> {vehicle.color}</p>
                        <p><strong>Type:</strong> {getVehicleType(vehicle)}</p>
                    </div>
                </div>
            )}

            {/* Photo Upload Section */}
            {vehicle && (
                <div className="photo-upload-section">
                    <div className="photo-upload-section-header">
                        <h2>Upload Photos ({existingPhotos ? existingPhotos.length : 0}/{currentPhotoTemplate.length})</h2>
                        <button
                            className="download-all-button"
                            onClick={handleDownloadAll}
                            disabled={downloadingAll || !existingPhotos || existingPhotos.length === 0}
                        >
                            {downloadingAll ? 'Zipping...' : 'Download All'}
                        </button>
                    </div>

                    <div className="bulk-upload-trigger">
                        <button
                            className="bulk-upload-button"
                            onClick={() => setBulkUploadOpen(true)}
                        >
                            Bulk Upload Photos
                        </button>
                    </div>

                    {/* Exterior Photos */}
                    <div className="photo-category">
                        <h3>Exterior Photos ({existingExteriorCount || 0}/{exteriorPhotos.length})</h3>
                        {loadingPhotos && <p>Loading existing photos...</p>}
                        <div className="photo-grid">
                            {exteriorPhotos.map((template, index) => {
                                if (template.multiPhoto) {
                                    return (
                                        <PhotoSlot
                                            key={index}
                                            template={template}
                                            uploadedPhotos={uploadedPhotos}
                                            existingPhotos={existingPhotos}
                                            deletingPhotos={deletingPhotos}
                                            updatingPhotos={updatingPhotos}
                                            downloadingPhotos={downloadingPhotos}
                                            onFileSelect={processFile}
                                            onRemovePhoto={removePhoto}
                                            onDeleteExisting={handleDeleteExistingPhoto}
                                            onUpdateExisting={handleUpdateExistingPhoto}
                                            onDropForUpdate={handleUpdateExistingPhoto}
                                            onDownload={handleDownloadPhoto}
                                        />
                                    );
                                }
                                const existingPhoto = getExistingPhotoByTemplate(template);
                                const isDeleting = existingPhoto && deletingPhotos[existingPhoto.file_id];
                                const isUpdating = existingPhoto && updatingPhotos[existingPhoto.file_id];
                                const isDownloading = existingPhoto && downloadingPhotos[existingPhoto.file_id];

                                return (
                                    <PhotoSlot
                                        key={index}
                                        template={template}
                                        uploadedPhoto={uploadedPhotos[template.name]}
                                        existingPhoto={existingPhoto}
                                        isDeleting={isDeleting}
                                        isUpdating={isUpdating}
                                        isDownloading={isDownloading}
                                        onFileSelect={processFile}
                                        onRemovePhoto={removePhoto}
                                        onDeleteExisting={handleDeleteExistingPhoto}
                                        onUpdateExisting={handleUpdateExistingPhoto}
                                        onDropForUpdate={handleUpdateExistingPhoto}
                                        onDownload={handleDownloadPhoto}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Interior Photos */}
                    <div className="photo-category">
                        <h3>Interior Photos ({existingInteriorCount || 0}/{interiorPhotos.length})</h3>
                        <div className="photo-grid">
                            {interiorPhotos.map((template, index) => {
                                if (template.multiPhoto) {
                                    return (
                                        <PhotoSlot
                                            key={index}
                                            template={template}
                                            uploadedPhotos={uploadedPhotos}
                                            existingPhotos={existingPhotos}
                                            deletingPhotos={deletingPhotos}
                                            updatingPhotos={updatingPhotos}
                                            downloadingPhotos={downloadingPhotos}
                                            onFileSelect={processFile}
                                            onRemovePhoto={removePhoto}
                                            onDeleteExisting={handleDeleteExistingPhoto}
                                            onUpdateExisting={handleUpdateExistingPhoto}
                                            onDropForUpdate={handleUpdateExistingPhoto}
                                            onDownload={handleDownloadPhoto}
                                        />
                                    );
                                }
                                const existingPhoto = getExistingPhotoByTemplate(template);
                                const isDeleting = existingPhoto && deletingPhotos[existingPhoto.file_id];
                                const isUpdating = existingPhoto && updatingPhotos[existingPhoto.file_id];
                                const isDownloading = existingPhoto && downloadingPhotos[existingPhoto.file_id];

                                return (
                                    <PhotoSlot
                                        key={index}
                                        template={template}
                                        uploadedPhoto={uploadedPhotos[template.name]}
                                        existingPhoto={existingPhoto}
                                        isDeleting={isDeleting}
                                        isUpdating={isUpdating}
                                        isDownloading={isDownloading}
                                        onFileSelect={processFile}
                                        onRemovePhoto={removePhoto}
                                        onDeleteExisting={handleDeleteExistingPhoto}
                                        onUpdateExisting={handleUpdateExistingPhoto}
                                        onDropForUpdate={handleUpdateExistingPhoto}
                                        onDownload={handleDownloadPhoto}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Detail Photos */}
                    <div className="photo-category">
                        <h3>Detail Photos ({existingDetailCount || 0}/{detailPhotos.length})</h3>
                        <div className="photo-grid">
                            {detailPhotos.map((template, index) => {
                                if (template.multiPhoto) {
                                    return (
                                        <PhotoSlot
                                            key={index}
                                            template={template}
                                            uploadedPhotos={uploadedPhotos}
                                            existingPhotos={existingPhotos}
                                            deletingPhotos={deletingPhotos}
                                            updatingPhotos={updatingPhotos}
                                            downloadingPhotos={downloadingPhotos}
                                            onFileSelect={processFile}
                                            onRemovePhoto={removePhoto}
                                            onDeleteExisting={handleDeleteExistingPhoto}
                                            onUpdateExisting={handleUpdateExistingPhoto}
                                            onDropForUpdate={handleUpdateExistingPhoto}
                                            onDownload={handleDownloadPhoto}
                                        />
                                    );
                                }
                                const existingPhoto = getExistingPhotoByTemplate(template);
                                const isDeleting = existingPhoto && deletingPhotos[existingPhoto.file_id];
                                const isUpdating = existingPhoto && updatingPhotos[existingPhoto.file_id];
                                const isDownloading = existingPhoto && downloadingPhotos[existingPhoto.file_id];

                                return (
                                    <PhotoSlot
                                        key={index}
                                        template={template}
                                        uploadedPhoto={uploadedPhotos[template.name]}
                                        existingPhoto={existingPhoto}
                                        isDeleting={isDeleting}
                                        isUpdating={isUpdating}
                                        isDownloading={isDownloading}
                                        onFileSelect={processFile}
                                        onRemovePhoto={removePhoto}
                                        onDeleteExisting={handleDeleteExistingPhoto}
                                        onUpdateExisting={handleUpdateExistingPhoto}
                                        onDropForUpdate={handleUpdateExistingPhoto}
                                        onDownload={handleDownloadPhoto}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Upload Button */}
                    <div className="upload-actions">
                        <button
                            className="upload-all-button"
                            onClick={handleUploadToCloud}
                            disabled={uploading || Object.keys(uploadedPhotos).length === 0}
                        >
                            {uploading ? 'Uploading...' : `Upload ${Object.keys(uploadedPhotos).length} Photo(s)`}
                        </button>
                    </div>
                </div>
            )}

            <BulkPhotoUpload
                isOpen={bulkUploadOpen}
                onClose={() => setBulkUploadOpen(false)}
                photoTemplate={currentPhotoTemplate}
                existingPhotos={existingPhotos}
                uploadedPhotos={uploadedPhotos}
                onAssignPhotos={handleBulkAssign}
                uploading={uploading}
            />
        </div>
    );
}

export default UploadVehiclePhotos;